import { Box, Text } from '@chakra-ui/react';
import { useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'app/store';
import {
  fetchSongEffect,
  getLyrics2ConnectionEffect,
  setLyricsSheetVersionEffect,
} from 'app/store/SongList/effects';
import { LyricsV2 } from 'app/components/Song/Lyrics/LyricsV2';
import { useSelector } from 'react-redux';
import {
  getAudioPlayer,
  getPlayerHeight,
} from 'app/store/AudioPlayer/selectors';
import { useNavigate } from 'react-router-dom';
import { useClickedOutsideElement } from 'app/utils/hooks';
import { getSetLyricsSheetVersionLink } from 'app/store/SongList/selectors';
import { LyricsVersion } from 'types/lyrics.types';

export const AudioPlayerLyricsView = () => {
  const { queue, queueIndex, isOpen } = useAppSelector(getAudioPlayer);
  const audio = queue[queueIndex];

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const playerHeight = useSelector(getPlayerHeight);

  useEffect(() => {
    if (!isOpen) navigate(-1);
  }, [isOpen, navigate]);

  const setLyricsVersionLink = useAppSelector(getSetLyricsSheetVersionLink);
  const setLyricsVersion = useCallback(
    async (version: LyricsVersion) => {
      try {
        await dispatch(setLyricsSheetVersionEffect({ version })).unwrap();
      } catch (e) {
        if (audio) {
          await dispatch(fetchSongEffect({ guid: audio.parentGuid! }));
        }
      }
    },
    [audio, dispatch]
  );

  useEffect(() => {
    if (setLyricsVersionLink) {
      setLyricsVersion(2);
    }
  }, [setLyricsVersion, setLyricsVersionLink]);

  useEffect(() => {
    const init = async () => {
      if (audio) {
        const guid = audio.parentGuid || audio.songGuid || '';
        const { lyricsVersion } = await dispatch(
          fetchSongEffect({ guid })
        ).unwrap();
        if (lyricsVersion?.version === 2) {
          dispatch(
            getLyrics2ConnectionEffect({
              songGuid: guid,
              songName: audio.songTitle!,
            })
          );
        }
      }
    };
    init();
  }, [audio, dispatch]);

  const { ref } = useClickedOutsideElement<HTMLDivElement>((event) => {
    if (document.body.clientHeight - event.y < playerHeight) {
      return;
    }
    navigate(-1);
  });

  if (!isOpen) return null;

  return (
    <Box ref={ref} data-testid="player-lyrics" pt={2}>
      <LyricsV2
        readonly
        emptyElement={
          <Text pt={6} color="textSubdued">
            This song doesn&apos;t have any lyrics yet.
          </Text>
        }
      >
        <Box maxWidth={640} width="full" mx="auto">
          <Text fontSize="xxs" fontWeight={500} opacity={0.72} mb={0.5}>
            Lyrics
          </Text>
          <Text fontSize="lg" fontWeight={500}>
            {audio.songTitle}
          </Text>
        </Box>
      </LyricsV2>
    </Box>
  );
};
